import { useMemo, useState } from "react";

import { TInputTag } from "components/InputMultipleTag";

import { queryGetTicketList } from "@type/api/management";
import { useQueryWithCallbacks } from "lib/query/useQueryWithCallbacks";

import {
  TGetTicketListReq,
  TGetTicketListRes,
} from "@type/api/management/types";

interface UseGetTicketListOptionsProps {
  idBu: string;
  excludedTicketCode?: string;
  idPipeline?: string;
  enabled?: boolean;
  onSuccess?: (res: TGetTicketListRes) => void;
}

export const useGetTicketListOptions = ({
  idBu,
  excludedTicketCode,
  idPipeline,
  enabled = true,
  onSuccess,
}: UseGetTicketListOptionsProps) => {
  const [ticketListOptions, setTicketListOptions] = useState<TInputTag[]>([]);

  const [data, setData] = useState({ page: 1, pageSize: 50, filter: {} });

  const params: TGetTicketListReq = useMemo(
    () => ({
      idBu,
      data: {
        ...data,
        filter: {
          ...data.filter,
          idPipeline,
        },
      },
    }),
    [idBu, data, idPipeline]
  );

  const queryGetTicketListKey = useMemo(() => {
    return queryGetTicketList._key(params);
  }, [params]);

  const { isLoading: isLoadingGetTicketListOptions } = useQueryWithCallbacks({
    queryKey: queryGetTicketListKey,
    enabled: !!params?.idBu && enabled,
    queryFn: () => queryGetTicketList(params),
    onSuccess: (res) => {
      const data = res?.data ?? [];

      // TODO: Ask BE Team to support exclude ticket code filter
      const filteredData = excludedTicketCode
        ? data.filter(({ code }) => !code.includes(excludedTicketCode))
        : data;

      const newOptions: TInputTag[] = filteredData.map(
        ({ id, title, code }) => ({
          id,
          name: `${code} ${title}`,
        })
      );
      setTicketListOptions((prev) => [...prev, ...newOptions]);
      if (newOptions.length > 0) {
        setData((prev) => ({ ...prev, page: prev.page + 1 }));
      }
      onSuccess?.(res);
    },
  });

  return {
    ticketListOptions,
    queryGetTicketListKey,
    isLoadingGetTicketListOptions,
  };
};
